import { GoogleMap, Polyline, Marker } from '@react-google-maps/api';
import { LoadingState } from '@/components/ui/loading-state';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';
import { Loader2 } from 'lucide-react';
import { logger } from '@/lib/logger';
import { StravaStyleMarker } from './StravaStyleMarker';
import { MapFallback } from './MapFallback';
import { useTrailColor } from '@/hooks/useTrailColor';

interface LatLng {
  lat: number;
  lng: number;
}

interface RunMapProps {
  polyline: LatLng[];
  center: LatLng;
  zoom?: number;
  avatarUrl?: string;
  accuracy?: number; // GPS accuracy in meters
  showPaceHeatmap?: boolean; // Enable pace-based color coding
  averagePace?: number; // Average pace in min/km for reference
}

const darkMapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#1a1a1a' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#000000' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }]
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#263c3f' }]
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b9a76' }]
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#2c2c2c' }]
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#1a1a1a' }]
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#8a8a8a' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#3c3c3c' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#1f1f1f' }]
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#f3d19c' }]
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#2f3948' }]
  },
  {
    featureType: 'transit.station',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#0f1419' }]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#515c6d' }]
  },
  {
    featureType: 'water',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#000000' }]
  }
];

const startFlagIcon = {
  path: 'M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z',
  fillColor: '#22c55e',
  fillOpacity: 1,
  strokeColor: '#FFFFFF',
  strokeWeight: 2,
  scale: 1.2,
  anchor: { x: 12, y: 24 } as google.maps.Point,
};

const endFlagIcon = {
  path: 'M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z',
  fillColor: '#ef4444',
  fillOpacity: 1,
  strokeColor: '#FFFFFF',
  strokeWeight: 2,
  scale: 1.2,
  anchor: { x: 12, y: 24 } as google.maps.Point,
};

export const RunMap = ({ 
  polyline, 
  center, 
  zoom = 15, 
  avatarUrl, 
  accuracy,
  showPaceHeatmap = true,
  averagePace
}: RunMapProps) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const { trailColor } = useTrailColor();

  // Calculate pace-based color for heatmap
  const getPaceColor = (speed: number, avgPace?: number): string => {
    if (!showPaceHeatmap || !speed || speed === 0) {
      return trailColor; // Fallback to user's trail color
    }

    // Convert speed (m/s) to pace (min/km)
    const paceMinPerKm = 1000 / (speed * 60);

    // If we have average pace, use it as reference
    // Otherwise use general running paces
    const referencePace = avgPace || 6.0; // 6 min/km as default reference

    const paceRatio = paceMinPerKm / referencePace;

    // Color scale (Strava-style):
    // Fast (< 0.85x avg): Green #22c55e
    // Normal (0.85-1.15x avg): Yellow #eab308
    // Slow (> 1.15x avg): Red #ef4444
    // Very slow (> 1.5x avg): Dark red #dc2626

    if (paceRatio < 0.85) {
      return '#22c55e'; // Fast - Green
    } else if (paceRatio < 1.15) {
      return '#eab308'; // Normal - Yellow
    } else if (paceRatio < 1.5) {
      return '#ef4444'; // Slow - Red
    } else {
      return '#dc2626'; // Very slow - Dark Red
    }
  };

  // Calculate speed between two consecutive points
  const calculateSpeed = (point1: LatLng, point2: LatLng, timeDiffMs: number): number => {
    if (timeDiffMs === 0) return 0;

    // Haversine distance
    const R = 6371000; // Earth radius in meters
    const dLat = (point2.lat - point1.lat) * Math.PI / 180;
    const dLng = (point2.lng - point1.lng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    // Speed in m/s
    return distance / (timeDiffMs / 1000);
  };

  // Create path segments with pace-based colors
  const paceSegments = (): Array<{ path: LatLng[]; color: string }> => {
    if (polyline.length < 2) return [];

    const segments: Array<{ path: LatLng[]; color: string }> = [];
    const avgTimeDiffMs = 1000; // Assume 1 second between points as default

    for (let i = 0; i < polyline.length - 1; i++) {
      const point1 = polyline[i];
      const point2 = polyline[i + 1];

      const speed = calculateSpeed(point1, point2, avgTimeDiffMs);
      const color = getPaceColor(speed, averagePace);

      segments.push({
        path: [point1, point2],
        color
      });
    }

    return segments;
  };

  logger.info('RunMap load status', { component: 'RunMap', isLoaded, hasLoadError: !!loadError });

  if (loadError) {
    const errorMsg = loadError?.message || '';
    logger.error('RunMap load error', loadError, { component: 'RunMap', errorMsg });
    
    return (
      <div className="w-full h-full">
        <MapFallback 
          error="Não foi possível conectar ao Google Maps. Tente novamente em alguns instantes."
        />
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-muted animate-fade-in">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="w-full h-full animate-fade-in animate-scale-in">
      <GoogleMap
      zoom={zoom}
      center={center}
      mapContainerStyle={{ width: '100%', height: '100%' }}
      options={{
        disableDefaultUI: true,
        zoomControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        keyboardShortcuts: false,
        styles: darkMapStyles
      }}
    >
      {polyline.length > 1 && (
        <>
          {showPaceHeatmap ? (
            // Pace Heatmap Mode - Segmentos coloridos por velocidade
            paceSegments().map((segment, i) => (
              <Polyline
                key={`pace-${i}`}
                path={segment.path}
                options={{
                  strokeColor: segment.color,
                  strokeWeight: 3,
                  strokeOpacity: 1,
                  geodesic: true,
                  zIndex: 3
                }}
              />
            ))
          ) : (
            // Single Color Mode - Estilo original com glow
            <>
              {/* Glow externo (suave e fino) */}
              <Polyline
                path={polyline}
                options={{
                  strokeColor: trailColor,
                  strokeWeight: 5,
                  strokeOpacity: 0.25,
                  geodesic: true,
                  zIndex: 1
                }}
              />
              
              {/* Glow interno (médio) */}
              <Polyline
                path={polyline}
                options={{
                  strokeColor: trailColor,
                  strokeWeight: 3,
                  strokeOpacity: 0.5,
                  geodesic: true,
                  zIndex: 2
                }}
              />
              
              {/* Linha principal (sólida e fina) */}
              <Polyline
                path={polyline}
                options={{
                  strokeColor: trailColor,
                  strokeWeight: 2,
                  strokeOpacity: 1,
                  geodesic: true,
                  zIndex: 3
                }}
              />
            </>
          )}

          {/* Marcador de início (bandeira verde) */}
          <Marker
            position={polyline[0]}
            icon={startFlagIcon}
            zIndex={4}
          />

          {/* Marcador de fim (bandeira vermelha) */}
          <Marker
            position={polyline[polyline.length - 1]}
            icon={endFlagIcon}
            zIndex={4}
          />
        </>
      )}
      
      {polyline.length > 0 && (
        <StravaStyleMarker
          position={polyline[polyline.length - 1] || center}
          accuracy={accuracy}
          color={trailColor}
        />
      )}
      </GoogleMap>
    </div>
  );
};
